import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, setAuthToken } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { applyFounderQuizDraft } from "../lib/founderQuizDraft";
import { applyProjectQuizDraft } from "../lib/projectQuiz";
import { GoogleLogin } from "@react-oauth/google";

export default function Login() {
  // Fresh login attempt → David's welcome popup becomes eligible again.
  useEffect(() => {
    try { sessionStorage.removeItem("clinq_david_popup_seen"); } catch (_) { /* ignore */ }
  }, []);
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Only honor same-origin path redirects (e.g. /join-team/<token>)
  const redirectTo = (params.get("redirect") || "").startsWith("/")
    ? params.get("redirect") : null;
  const { refresh, setUser } = useAuth();

  // Founders land on the page matching their journey stage — deduced BEFORE
  // navigating so they never see the dashboard flash then bounce to onboarding.
  const founderDest = async () => {
    try {
      const { data: c } = await api.get("/companies/me");
      if ((c?.kind || "business") === "project") {
        return c?.prs_analysis ? "/project/report" : "/project/intake";
      }
      const { data: me } = await api.get("/auth/me").catch(() => ({ data: null }));
      if (me?.pathway === "direct") return "/founder/apply";
      return c?.report_downloaded_at ? "/founder/dashboard" : "/onboarding";
    } catch (_) {
      return "/onboarding";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setUnverified(false); setResendState("idle");
    try {
      const { data } = await api.post("/auth/login", form);
      // Persist the JWT for Bearer-header auth (cookie auth doesn't survive
      // the preview ingress's `Access-Control-Allow-Origin: *` header).
      if (data?.token) setAuthToken(data.token);
      // Hydrate the auth context synchronously from the login response so
      // ProtectedRoute sees the user on the very next render — then AWAIT the
      // full refresh so founder gate fields (company_kind, quiz_completed_at)
      // are present before we navigate into a gated route.
      setUser({
        user_id: data.user_id, email: data.email,
        name: data.name, role: data.role,
      });
      await refresh();
      if (data.role === "founder") {
        const isProject = await applyProjectQuizDraft(api);
        if (!isProject) await applyFounderQuizDraft(api, { mergeOnlyEmpty: true });
      }
      const dest = redirectTo
                 ? redirectTo
                 : data.role === "funder" ? "/funder/terminal"
                 : data.role === "admin"  ? "/admin/dashboard"
                 : data.role === "studio" ? "/studio/dashboard"
                 : data.role === "advisor" ? "/advisor/dashboard"
                 : data.role === "rm" ? "/rm/dashboard"
                 : await founderDest();
      nav(dest);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && typeof detail === "string" && detail.toLowerCase().includes("verify your email")) {
        setUnverified(true);
      } else {
        setErr(detail || e.message);
      }
    } finally { setBusy(false); }
  };

  const resendVerification = async () => {
    setResendState("sending");
    try {
      await api.post("/auth/resend-verification", { email: form.email.trim().toLowerCase() });
      setResendState("sent");
    } catch (_) {
      setResendState("idle");
    }
  };

  // Direct Google OAuth via @react-oauth/google. The `<GoogleLogin>` button
  // returns a Google-signed JWT credential which we POST to /api/auth/google
  // for server-side signature verification, then our standard JWT cookie is set.
  //
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const onGoogleSuccess = async (credResp) => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/google", {
        credential: credResp.credential,
        role: "founder",  // existing users keep their saved role; this only applies if it's a brand-new account
      });
      if (data?.token) setAuthToken(data.token);
      await refresh();
      if (data.role === "founder") {
        const isProject = await applyProjectQuizDraft(api);
        if (!isProject) await applyFounderQuizDraft(api, { mergeOnlyEmpty: true });
      }
      const dest = redirectTo
                 ? redirectTo
                 : data.role === "funder" ? "/funder/terminal"
                 : data.role === "admin"  ? "/admin/dashboard"
                 : data.role === "studio" ? "/studio/dashboard"
                 : data.role === "advisor" ? "/advisor/dashboard"
                 : data.role === "rm" ? "/rm/dashboard"
                 : await founderDest();
      nav(dest);
    } catch (e) {
      setErr(e.response?.data?.detail || "Google sign-in failed");
    } finally { setBusy(false); }
  };
  const onGoogleError = () => setErr("Google sign-in was cancelled or failed.");

  return (
    <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
      {busy && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-[#F4F8F8]/90 backdrop-blur-sm" data-testid="login-loading-overlay">
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-[#0A8B85] border-t-transparent" />
          <p className="text-sm font-semibold text-neutral-600">Signing you in — taking you to the right place…</p>
        </div>
      )}
      <div className="mx-auto max-w-md px-4 py-12 sm:px-6 sm:py-16">
        <Link to="/" className="text-2xl font-extrabold text-[#0A8B85]">Clinq</Link>
        <h1 className="mt-6 text-2xl font-bold text-neutral-900 sm:mt-8 sm:text-3xl">Welcome back</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input data-testid="login-email" type="email" placeholder="Email" required value={form.email}
            onChange={e => setForm({...form, email: e.target.value})}
            className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]" />
          <input data-testid="login-password" type="password" placeholder="Password" required value={form.password}
            onChange={e => setForm({...form, password: e.target.value})}
            className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]" />
          <div className="flex justify-end">
            <Link to="/forgot-password" data-testid="login-forgot-link"
                  className="text-xs font-semibold text-[#0A8B85] hover:underline">
              Forgot password?
            </Link>
          </div>
          {err && <p className="text-sm text-red-600" data-testid="login-error">{String(err)}</p>}
          {unverified && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="login-unverified-notice">
              <p className="text-sm font-semibold text-amber-900">Please verify your email address before logging in.</p>
              <p className="mt-1 text-xs text-amber-800">Check your inbox (and spam folder) for the verification email we sent when you signed up.</p>
              {resendState === "sent" ? (
                <p className="mt-3 text-xs font-semibold text-emerald-700" data-testid="login-resend-sent">
                  Sent! A fresh verification email is on its way to {form.email}.
                </p>
              ) : (
                <button type="button" data-testid="login-resend-btn" onClick={resendVerification} disabled={resendState === "sending"}
                  className="mt-3 rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                  {resendState === "sending" ? "Sending…" : "Resend verification email"}
                </button>
              )}
            </div>
          )}
          <button data-testid="login-submit" disabled={busy} type="submit"
            className="w-full rounded-lg bg-[#0A8B85] py-3 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <div className="my-5 flex items-center gap-3 text-xs text-neutral-400">
          <div className="h-px flex-1 bg-neutral-200" /> OR <div className="h-px flex-1 bg-neutral-200" />
        </div>
        <div className="flex max-w-full justify-center overflow-hidden" data-testid="login-google">
          <GoogleLogin
            onSuccess={onGoogleSuccess}
            onError={onGoogleError}
            text="continue_with"
            shape="rectangular"
            theme="outline"
            size="large"
            width={Math.min(384, (typeof window !== "undefined" ? window.innerWidth : 384) - 96)}
          />
        </div>
        <p className="mt-6 text-center text-sm text-neutral-600">
          No account?{" "}
          <Link to="/join" className="font-semibold text-[#0A8B85] hover:underline">Sign up</Link>
        </p>
      </div>
    </div>
  );
}
